import { useEffect, type ReactNode } from 'react'
import { Links, Meta, Outlet, Scripts, ScrollRestoration, useLocation } from 'react-router'
import { MotionConfig } from 'framer-motion'
import type { Route } from './+types/root'
import stylesheet from './index.css?url'
import { WhatsAppButton } from '@/components/WhatsAppButton'
import { ErrorFallback } from '@/components/ErrorFallback'
import { NavigationProgress } from '@/components/design/NavigationProgress'
import { JsonLd } from '@/lib/seo'
import { useNonce } from '@/lib/nonce'
import { siteStructuredData } from '@/lib/siteStructuredData'
import { crossSiteGuard } from '@/lib/lms/identity/middleware.server'
import { isPortalPath } from '@/lib/portal/paths'

const GOOGLE_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Geist:wght@300..800&family=Geist+Mono:wght@400..600&family=Hind+Siliguri:wght@400;500;600;700&family=Caveat:wght@500..700&display=swap'

export const middleware: Route.MiddlewareFunction[] = [crossSiteGuard]

export const links: Route.LinksFunction = () => [
  { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/images/favicon-32.png' },
  { rel: 'apple-touch-icon', href: '/images/apple-touch-icon.png' },
  { rel: 'sitemap', type: 'application/xml', title: 'Sitemap', href: '/sitemap.xml' },
  { rel: 'alternate', type: 'text/plain', title: 'LLM summary', href: '/llms.txt' },
  { rel: 'alternate', type: 'text/plain', title: 'Full LLM content', href: '/llms-full.txt' },
  { rel: 'author', href: '/humans.txt' },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  { rel: 'stylesheet', href: GOOGLE_FONTS_URL },
  { rel: 'stylesheet', href: stylesheet },
]

export function Layout({ children }: { children: ReactNode }) {
  const nonce = useNonce()

  return (
    <html lang="en">
      <head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#07080A" />
        <meta name="author" content="Skillo Academy" />
        <Meta />
        <Links />
        <script
          defer
          data-domain="skillo.minhajsadik.com"
          src="https://plausible.io/js/script.js"
        />
        <JsonLd data={siteStructuredData} />
      </head>
      <body>
        {children}
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  )
}

export default function App() {
  const { pathname } = useLocation()

  return (
    <MotionConfig reducedMotion="user">
      <NavigationProgress />
      <Outlet />
      {!isPortalPath(pathname) && <WhatsAppButton />}
    </MotionConfig>
  )
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  useEffect(() => {
    console.error('Route error:', error)
  }, [error])

  return <ErrorFallback />
}
